
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import SEOHead from '../../components/SEOHead';

interface ProjectReport {
  id: string;
  title: string;
  status: string;
  progress: number;
  tasksCompleted: number;
  totalTasks: number;
  budget: string;
  spent: string;
  team: number;
  daysRemaining: number;
  start_date?: string;
  end_date?: string;
}

interface TaskReport {
  id: string;
  title: string;
  project: string;
  assignee: string;
  status: string;
  priority: string;
  due_date: string;
  overdue: boolean;
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'tasks' | 'performance'>('overview');
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [projectReports, setProjectReports] = useState<ProjectReport[]>([]);
  const [taskReports, setTaskReports] = useState<TaskReport[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadReportsData();
    }
  }, [user, dateRange]);

  const loadReportsData = async () => {
    try {
      setLoading(true);
      
      // Carregar projetos do usuário
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user?.id);

      if (projectsError) {
        console.error('Erro ao carregar projetos:', projectsError);
      } else if (projects) {
        // Transformar dados dos projetos para o formato do relatório
        const projectReportsData: ProjectReport[] = projects.map(project => {
          const startDate = project.start_date ? new Date(project.start_date) : new Date();
          const endDate = project.end_date ? new Date(project.end_date) : new Date();
          const today = new Date();
          const daysRemaining = project.status === 'Concluído' ? 0 : Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
          
          return {
            id: project.id,
            title: project.title,
            status: project.status,
            progress: project.progress || 0,
            tasksCompleted: 0, // Será calculado quando carregarmos as tarefas
            totalTasks: 0, // Será calculado quando carregarmos as tarefas
            budget: 'R$ 0',
            spent: 'R$ 0',
            team: 1,
            daysRemaining,
            start_date: project.start_date,
            end_date: project.end_date
          };
        });
        
        setProjectReports(projectReportsData);
      }

      // Carregar tarefas do usuário
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select(`
          *,
          projects!inner(user_id, title)
        `)
        .eq('projects.user_id', user?.id);

      if (tasksError) {
        console.error('Erro ao carregar tarefas:', tasksError);
      } else if (tasks) {
        // Transformar dados das tarefas para o formato do relatório
        const taskReportsData: TaskReport[] = tasks.map(task => {
          const dueDate = task.due_date ? new Date(task.due_date) : new Date();
          const today = new Date();
          const overdue = task.status !== 'Concluída' && dueDate < today;
          
          return {
            id: task.id,
            title: task.title,
            project: task.projects?.title || 'Projeto não encontrado',
            assignee: task.assignee || 'Não atribuído',
            status: task.status,
            priority: task.priority,
            due_date: task.due_date || '',
            overdue
          };
        });
        
        setTaskReports(taskReportsData);

        // Atualizar contadores de tarefas nos projetos
        setProjectReports(prev => prev.map(project => {
          const projectTasks = taskReportsData.filter(task => task.project === project.title);
          const completedTasks = projectTasks.filter(task => task.status === 'Concluída').length;
          
          return {
            ...project,
            totalTasks: projectTasks.length,
            tasksCompleted: completedTasks
          };
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar dados dos relatórios:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo': return 'bg-green-100 text-green-800';
      case 'Concluído': return 'bg-blue-100 text-blue-800';
      case 'Em Andamento': return 'bg-yellow-100 text-yellow-800';
      case 'Pausado': return 'bg-gray-100 text-gray-800';
      case 'Pendente': return 'bg-orange-100 text-orange-800';
      case 'Concluída': return 'bg-blue-100 text-blue-800';
      case 'Arquivado': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Alta': return 'bg-red-100 text-red-800';
      case 'Média': return 'bg-yellow-100 text-yellow-800';
      case 'Baixa': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalProjects = projectReports.length;
  const activeProjects = projectReports.filter(p => p.status === 'Ativo' || p.status === 'Em Andamento').length;
  const completedProjects = projectReports.filter(p => p.status === 'Concluído').length;
  const totalTasks = taskReports.length;
  const overdueTasks = taskReports.filter(t => t.overdue).length;
  const completedTasks = taskReports.filter(t => t.status === 'Concluída').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Relatórios e Análises - Vibecode",
    "description": "Acompanhe o desempenho dos seus projetos e equipe com relatórios detalhados e análises de produtividade.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/reports`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Dashboard",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/dashboard`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Relatórios",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/reports`
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Relatórios e Análises - Vibecode"
        description="Acompanhe o desempenho dos seus projetos e equipe com relatórios detalhados e análises de produtividade."
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/reports`}
        jsonLd={jsonLd}
      />

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button className="mr-4 text-gray-600 hover:text-gray-900 cursor-pointer">
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <h1 className="text-2xl font-bold text-indigo-600" style={{ fontFamily: "Pacifico, serif" }}>
                Vibecode
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a 
                onClick={() => navigate('/dashboard')}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Dashboard
              </a>
              <a href="#" className="text-indigo-600 font-medium cursor-pointer">Relatórios</a>
              <a 
                onClick={() => navigate('/profile')}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Perfil
              </a>
              <a 
                onClick={() => navigate('/')}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Início
              </a>
            </nav>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-notification-line text-xl"></i>
              </button>
              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">U</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Relatórios e Análises</h1>
          <p className="text-gray-600">Acompanhe o desempenho dos seus projetos e equipe</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Período:</label>
              <div className="relative">
                <select 
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm appearance-none"
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                  <option value="365">Último ano</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>
            </div>
            <div className="flex space-x-3">
              <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-download-line mr-2"></i>
                Exportar PDF
              </button>
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-file-excel-line mr-2"></i>
                Exportar Excel
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === 'overview'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Visão Geral
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === 'projects'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Projetos
              </button>
              <button
                onClick={() => setActiveTab('tasks')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === 'tasks'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Tarefas
              </button>
              <button
                onClick={() => setActiveTab('performance')}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === 'performance'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Performance
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total de Projetos</p>
                        <p className="text-3xl font-bold">{totalProjects}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-400/30 rounded-lg flex items-center justify-center">
                        <i className="ri-folder-line text-2xl"></i>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Projetos Ativos</p>
                        <p className="text-3xl font-bold">{activeProjects}</p>
                      </div>
                      <div className="w-12 h-12 bg-green-400/30 rounded-lg flex items-center justify-center">
                        <i className="ri-play-circle-line text-2xl"></i>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Projetos Concluídos</p>
                        <p className="text-3xl font-bold">{completedProjects}</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-400/30 rounded-lg flex items-center justify-center">
                        <i className="ri-check-circle-line text-2xl"></i>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">Tarefas em Atraso</p>
                        <p className="text-3xl font-bold">{overdueTasks}</p>
                      </div>
                      <div className="w-12 h-12 bg-orange-400/30 rounded-lg flex items-center justify-center">
                        <i className="ri-alarm-warning-line text-2xl"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Status dos Projetos</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Ativos</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: totalProjects > 0 ? `${(activeProjects / totalProjects) * 100}%` : '0%' }}></div>
                          </div>
                          <span className="text-sm font-medium">{activeProjects}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Concluídos</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: totalProjects > 0 ? `${(completedProjects / totalProjects) * 100}%` : '0%' }}></div>
                          </div>
                          <span className="text-sm font-medium">{completedProjects}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Arquivados</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div className="bg-gray-500 h-2 rounded-full" style={{ width: totalProjects > 0 ? `${((totalProjects - activeProjects - completedProjects) / totalProjects) * 100}%` : '0%' }}></div>
                          </div>
                          <span className="text-sm font-medium">{totalProjects - activeProjects - completedProjects}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Produtividade da Equipe</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Tarefas Concluídas</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: totalTasks > 0 ? `${(completedTasks / totalTasks) * 100}%` : '0%' }}></div>
                          </div>
                          <span className="text-sm font-medium">{completedTasks}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Em Andamento</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div className="bg-yellow-500 h-2 rounded-full" style={{ width: totalTasks > 0 ? `${((totalTasks - completedTasks - overdueTasks) / totalTasks) * 100}%` : '0%' }}></div>
                          </div>
                          <span className="text-sm font-medium">{totalTasks - completedTasks - overdueTasks}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Em Atraso</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div className="bg-red-500 h-2 rounded-full" style={{ width: totalTasks > 0 ? `${(overdueTasks / totalTasks) * 100}%` : '0%' }}></div>
                          </div>
                          <span className="text-sm font-medium">{overdueTasks}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Relatório de Projetos</h3>
                {projectReports.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-folder-line text-4xl text-gray-400 mb-4"></i>
                    <p className="text-gray-500">Nenhum projeto encontrado</p>
                    <p className="text-sm text-gray-400">Crie seu primeiro projeto no dashboard</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Projeto
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Progresso
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tarefas
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prazo
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {projectReports.map((project) => (
                          <tr key={project.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{project.title}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                                {project.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                  <div 
                                    className="bg-indigo-500 h-2 rounded-full" 
                                    style={{ width: `${project.progress}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm text-gray-600">{project.progress}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {project.tasksCompleted}/{project.totalTasks}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {project.daysRemaining > 0 ? `${project.daysRemaining} dias` : 'Concluído'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Relatório de Tarefas</h3>
                {taskReports.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-task-line text-4xl text-gray-400 mb-4"></i>
                    <p className="text-gray-500">Nenhuma tarefa encontrada</p>
                    <p className="text-sm text-gray-400">Crie tarefas nos seus projetos</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tarefa
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Projeto
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Responsável
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prioridade
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prazo
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {taskReports.map((task) => (
                          <tr key={task.id} className={`hover:bg-gray-50 ${task.overdue ? 'bg-red-50' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{task.title}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {task.project}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {task.assignee}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                {task.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`text-sm ${task.overdue ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                                {task.due_date ? new Date(task.due_date).toLocaleDateString('pt-BR') : 'Sem prazo'}
                                {task.overdue && (
                                  <span className="ml-2">
                                    <i className="ri-alarm-warning-line"></i>
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && (
              <div className="space-y-8">
                <h3 className="text-lg font-semibold text-gray-900">Análise de Performance</h3>
                
                {/* Performance Metrics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Métricas de Produtividade</h4>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Taxa de Conclusão de Projetos</span>
                        <span className="text-sm font-medium text-green-600">
                          {totalProjects > 0 ? Math.round((completedProjects / totalProjects) * 100) : 0}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Taxa de Conclusão de Tarefas</span>
                        <span className="text-sm font-medium text-green-600">
                          {totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Tarefas em Atraso</span>
                        <span className="text-sm font-medium text-red-600">{overdueTasks}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Projetos Ativos</span>
                        <span className="text-sm font-medium text-blue-600">{activeProjects}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Resumo Geral</h4>
                    <div className="space-y-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">{totalProjects}</p>
                        <p className="text-sm text-gray-600">Total de Projetos</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{totalTasks}</p>
                        <p className="text-sm text-gray-600">Total de Tarefas</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {totalTasks > 0 ? Math.round(((totalTasks - overdueTasks) / totalTasks) * 100) : 100}%
                        </p>
                        <p className="text-sm text-gray-600">Taxa de Sucesso</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
